import { useMutation } from '@apollo/client'
import { useNavigate } from '@faceup/router'
import { Button, Popconfirm, Space, useMessage } from '@faceup/ui-base'
import { VerificationStatus } from '@faceup/utils'
import { graphql } from '../__generated__'
import { useAdminAccessRights } from '../Contexts/AdminAccessRightsProvider'

const mutations = {
  approvePartnerRegistrationMutation_mutation: graphql(`
    mutation approvePartnerRegistrationMutation_mutation(
      $input: ApprovePartnerRegistrationInput!
    ) {
      approvePartnerRegistration(input: $input) {
        partner {
          id
          verificationStatus
        }
      }
    }
  `),
  rejectPartnerRegistrationMutation_mutation: graphql(`
    mutation rejectPartnerRegistrationMutation_mutation(
      $input: RejectPartnerRegistrationInput!
    ) {
      rejectPartnerRegistration(input: $input) {
        success
      }
    }
  `),
  deletePartnerMutation_mutation: graphql(`
    mutation deletePartnerMutation_mutation($input: DeletePartnerInput!) {
      deletePartner(input: $input) {
        success
      }
    }
  `),
}

type Props = {
  partnerId: string | null
  verificationStatus: VerificationStatus | null
}

const VerifyPartnerRegistrationButtons = ({ partnerId, verificationStatus }: Props) => {
  const { isAllowedPartnerApprove, isAllowedPartnerDelete } = useAdminAccessRights()
  const navigate = useNavigate()
  const message = useMessage()

  const [rejectPartner, { loading: rejectPartnerLoading }] = useMutation(
    mutations.rejectPartnerRegistrationMutation_mutation,
    {
      onCompleted: data => {
        if (data.rejectPartnerRegistration?.success) {
          message.success('Partner registration rejected')
          navigate(routes => routes.partners())
        } else {
          message.error('Failed to reject partner registration')
        }
      },
    }
  )
  const [approvePartner, { loading: approvePartnerLoading }] = useMutation(
    mutations.approvePartnerRegistrationMutation_mutation
  )
  const [deletePartner, { loading: deletePartnerLoading }] = useMutation(
    mutations.deletePartnerMutation_mutation,
    {
      onCompleted: data => {
        if (data.deletePartner?.success) {
          message.success('Partner deleted')
          navigate(routes => routes.partners())
        } else {
          message.error('Failed to delete partner')
        }
      },
    }
  )

  if (!partnerId) {
    return null
  }

  if (verificationStatus !== VerificationStatus.Pending) {
    return (
      <Popconfirm
        title='Delete partner'
        onConfirm={() => {
          deletePartner({
            variables: {
              input: {
                partnerId,
              },
            },
          })
        }}
      >
        <Button
          disabled={!isAllowedPartnerDelete || deletePartnerLoading}
          loading={deletePartnerLoading}
          type='primary'
          danger
        >
          Delete
        </Button>
      </Popconfirm>
    )
  }

  return (
    <Space>
      <Popconfirm
        title='Reject partner registration'
        onConfirm={() => {
          rejectPartner({
            variables: {
              input: {
                partnerId,
              },
            },
          })
        }}
      >
        <Button
          disabled={!isAllowedPartnerApprove || rejectPartnerLoading}
          loading={rejectPartnerLoading}
        >
          Reject
        </Button>
      </Popconfirm>

      <Popconfirm
        title='Approve partner registration'
        onConfirm={() => {
          approvePartner({
            variables: {
              input: {
                partnerId,
              },
            },
          })
        }}
      >
        <Button
          disabled={!isAllowedPartnerApprove || approvePartnerLoading}
          loading={approvePartnerLoading}
        >
          Approve
        </Button>
      </Popconfirm>
    </Space>
  )
}

export default VerifyPartnerRegistrationButtons
